import { ThoughtShareWith } from '@api/assessmentQuestionnaireApiHandler';
import { getCompanyDetails } from '@helpers/auth.helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { Box, Stack, TextField } from '@mui/material';
import { BorderedCheckbox, CustomSwipeableDrawer, MotionText, showToast } from '@shared/atoms';
import { ToastMessages } from 'constants/toastMessages';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RootState } from 'store';
import { addThought } from 'store/Slices/QuestionnaireSliceV2';
import * as yup from 'yup';
import { SystemButton } from '../SystemButton/SystemButton';
import styles from './index.module.scss';

const schema = yup.object().shape({
  thoughts: yup.string().max(280, 'Maximum 280 characters allowed'),
  shareWith: yup.string().required('Please select an option'),
});

export const ShareThoughtDrawer: React.FC<ShareThoughtDrawerInterface> = ({
  open,
  handleClose,
  handleOpen,
  questionId,
  questionLabel,
}) => {
  const dispatch = useAppDispatch();
  const { control, handleSubmit, watch, setValue, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      thoughts: '',
      shareWith: ThoughtShareWith.MYSELF,
    },
  });
  const { isDesktop } = useWindowDimensions();
  const [currentCompanyName, setCurrentCompanyName] = useState('');
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig.data);

  const { thoughtsList } = useAppSelector((state) => state.questionnaireV2 || {});

  const currentThoughtData =
    thoughtsList && thoughtsList?.find((th) => th.questionId === questionId);

  useEffect(() => {
    if (open) {
      if (currentThoughtData && currentThoughtData?.thought?.length > 0) {
        setValue('shareWith', currentThoughtData.shareWith);
        setValue('thoughts', currentThoughtData.thought);
      } else {
        setValue('shareWith', ThoughtShareWith.MYSELF);
        setValue('thoughts', '');
      }
    } else {
      reset({
        thoughts: '',
        shareWith: ThoughtShareWith.MYSELF,
      });
    }
  }, [open, questionId, currentThoughtData, setValue, reset]);

  useEffect(() => {
    const getCurrentCompanyName = async () => {
      const comDetails = await getCompanyDetails();
      if (comDetails) {
        setCurrentCompanyName(comDetails.company_name);
      }
    };

    getCurrentCompanyName();
  }, []);

  const onSubmit = (data: {
    thoughts: string;
    shareWith: ThoughtShareWith.COMPANY | ThoughtShareWith.MYSELF;
  }) => {
    dispatch(
      addThought({
        thought: data.thoughts,
        shareWith: data.shareWith,
        questionId,
      }),
    );
    showToast(ToastMessages.createSpeakUp.success.YOUR_THOUGHT_SAVED, undefined, 'success');
    handleClose();
  };

  const thoughts = watch('thoughts');
  const shareWith = watch('shareWith');

  const handleMyselfChange = () => {
    setValue('shareWith', ThoughtShareWith.MYSELF);
  };

  const handleCompanyChange = () => {
    setValue('shareWith', ThoughtShareWith.COMPANY);
  };

  const handleDrawerClose = () => {
    reset({
      thoughts: '',
      shareWith: ThoughtShareWith.MYSELF,
    });
    handleClose();
  };

  return (
    <CustomSwipeableDrawer
      open={open}
      onClose={handleDrawerClose}
      onOpen={handleOpen}
      anchor="right"
      customWidth={isDesktop ? '400px' : '100vw'}
    >
      <Box className={styles.container} sx={{ p: 3 }}>
        <MotionText as="h2" text="Share your thoughts" />
        <MotionText as="p" text={questionLabel} />

        <Controller
          name="thoughts"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              placeholder="What's on your mind?"
              multiline
              rows={6}
              fullWidth
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 280 }}
            />
          )}
        />
        <p className={styles.hsCharCounter}>{thoughts?.length || 0}/280 characters</p>

        <MotionText as="p" text="Share with:" />

        <Stack direction={isDesktop ? 'row' : 'column'} spacing={2} sx={{ mb: 2 }}>
          <BorderedCheckbox
            id={`mySelf-${questionId}`}
            text="Myself"
            color="#019DF2"
            onChange={handleMyselfChange}
            checked={shareWith === ThoughtShareWith.MYSELF}
          />
          <BorderedCheckbox
            id={`company-${questionId}`}
            text="Organisation"
            color="#019DF2"
            onChange={handleCompanyChange}
            checked={shareWith === ThoughtShareWith.COMPANY}
          />
        </Stack>

        <div className={styles.emptyBox}>
          {shareWith === ThoughtShareWith.MYSELF && (
            <div>
              Sharing thoughts reminds us why we felt a certain way, so when we look back, we
              understand ourselves, allowing us to enjoy the positives and learn from the negatives.
            </div>
          )}
          {shareWith === ThoughtShareWith.COMPANY && (
            <div className={styles.emptyBox}>
              {companyConfigData && companyConfigData?.shareThoughtOrganizationText ? (
                companyConfigData?.shareThoughtOrganizationText
              ) : (
                <div>
                  Sharing thoughts with {currentCompanyName} allows them to learn about your needs,
                  what you&apos;re struggling with, and what support should be offered. They can
                  also respond and show you&apos;ve been heard. Your thought will be anonymous.
                </div>
              )}
            </div>
          )}
        </div>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <SystemButton
            variant="primarySkyBlue"
            onClick={handleSubmit(onSubmit)}
            disabled={!thoughts || !shareWith}
            fullWidthButton
          >
            Share thought
          </SystemButton>
        </Box>
      </Box>
    </CustomSwipeableDrawer>
  );
};

type ShareThoughtDrawerInterface = {
  open: boolean;
  handleClose: () => void;
  handleOpen: () => void;
  questionId: number;
  questionLabel: string;
};
